import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { authService } from '@/services/auth';
import { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  onboardingCompleted: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  const refreshUser = async () => {
    try {
      const refreshed = await authService.refreshUser();
      setUser(refreshed);
      setOnboardingCompleted(refreshed.onboarding_completed ?? false);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Don't log out the user if refresh fails - keep existing session
      // Only clear user if it's an auth error (session invalid)
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = String(error.message);
        if (errorMessage.includes('JWT') || errorMessage.includes('session') || errorMessage.includes('token')) {
          // Session is invalid, clear user
          setUser(null);
          setOnboardingCompleted(false);
        }
        // Otherwise, keep the existing user state
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      const session = await supabase.auth.getSession();
      if (session.data.session?.user) {
        await refreshUser();
      }
      setLoading(false);
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) refreshUser();
      else {
        setUser(null);
        setOnboardingCompleted(false);
        // Clear car selection on logout
        try {
          localStorage.removeItem('selectedCarId');
        } catch (error) {
          console.warn('Failed to clear car selection on logout:', error);
        }
      }
    });

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, onboardingCompleted, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
